import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { getHmiLabelForCreatorPrimary } from "../constants/languages";
import { severityAccentColor, severityChipProps } from "../shared/severityChip";

interface Description {
  language_code: string;
  label: string;
}

export interface EventConclusionCardProps {
  kind: "rising" | "resolving";
  severity: string | number | null;
  descriptions: Description[] | null | undefined;
  primaryLanguage?: string | null;
  contractName?: string | null;
  /** When true, contract name is omitted (e.g. resolving inherits from rising in a pair). */
  hideContractName?: boolean;
}

export default function EventConclusionCard({
  kind,
  severity,
  descriptions,
  primaryLanguage,
  contractName,
  hideContractName = false,
}: EventConclusionCardProps) {
  const theme = useTheme();
  const isRising = kind === "rising";
  const accent = severityAccentColor(theme, severity);
  const title = isRising ? "Rising event" : "Resolving event";
  const Icon = isRising ? ArrowUpwardIcon : ArrowDownwardIcon;
  const label = getHmiLabelForCreatorPrimary(descriptions, primaryLanguage);
  const severityChip = severityChipProps(severity);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        borderLeftWidth: 4,
        borderLeftStyle: "solid",
        borderLeftColor: accent,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Icon sx={{ fontSize: 20, color: accent }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {title}
        </Typography>
      </Stack>

      <Stack spacing={1.25}>
        {!hideContractName && contractName && String(contractName).trim() && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Event contract name
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {String(contractName).trim()}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Severity
          </Typography>
          <Chip size="small" sx={{ mt: 0.25, ...severityChip.sx }} {...severityChip} />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            HMI label
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 500 }}>
            {label || "—"}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
